import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { useMonthOptions } from '../lib/useMonthOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Upload, Camera, Loader2, CheckCircle2, AlertTriangle, XCircle, Eye, Trash2, Car, Layers, Search, ChevronDown, ChevronUp, Check, Clock, Building2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { DocumentsToIdentify } from '../components/DocumentsToIdentify';
import { DatePickerField } from '../components/DatePickerField';
import { useConfirm } from '../components/ConfirmDialog';

function fmt(v) { return v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v) : '—'; }

const CONF = {
    pret: { label: 'Prêt', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    a_verifier: { label: 'À vérifier', color: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
    info_manquante: { label: 'Info manquante', color: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
};

const DEST_OPTIONS = [
    { value: 'frais_vehicule', label: 'Frais véhicule', Icon: Car, color: 'bg-blue-100 border-blue-300 text-blue-800', colorInactive: 'border-gray-200 text-gray-500' },
    { value: 'frais_agence', label: 'Frais d\'agence', Icon: Building2, color: 'bg-orange-100 border-orange-300 text-orange-800', colorInactive: 'border-gray-200 text-gray-500' },
    { value: 'retenue_commercial', label: 'Retenue commercial', Icon: UserRound, color: 'bg-purple-100 border-purple-300 text-purple-800', colorInactive: 'border-gray-200 text-gray-500' },
    { value: 'frais_mutualise', label: 'Frais mutualisé', Icon: Layers, color: 'bg-indigo-100 border-indigo-300 text-indigo-800', colorInactive: 'border-gray-200 text-gray-500' },
];

const CATEGORIES_FV = [
    { value: 'revision_entretien', label: 'Révision / entretien' }, { value: 'mecanique', label: 'Mécanique' },
    { value: 'pneumatiques', label: 'Pneumatiques' }, { value: 'carrosserie', label: 'Carrosserie' },
    { value: 'controle_technique', label: 'Contrôle technique' }, { value: 'nettoyage', label: 'Nettoyage' },
    { value: 'transport', label: 'Transport' }, { value: 'carte_grise', label: 'Carte grise' },
    { value: 'garantie', label: 'Garantie' }, { value: 'autre', label: 'Autre' },
];
const CATEGORIES_FM = [
    { value: 'lavage', label: 'Lavage' }, { value: 'produits_nettoyage', label: 'Produits nettoyage' },
    { value: 'lustrage_detailing', label: 'Lustrage / detailing' }, { value: 'pads_microfibres', label: 'Pads / microfibres' },
    { value: 'cles_piles', label: 'Clés / piles' }, { value: 'consommables_preparation', label: 'Consommables préparation' },
    { value: 'autre', label: 'Autre' },
];
const CATEGORIES_AGENCE = [
    { value: 'loyer', label: 'Loyer' }, { value: 'assurance', label: 'Assurance' },
    { value: 'energie', label: 'Énergie' }, { value: 'telephone', label: 'Téléphone' },
    { value: 'internet', label: 'Internet' }, { value: 'comptabilite', label: 'Comptabilité' },
    { value: 'logiciels', label: 'Logiciels' }, { value: 'publicite', label: 'Publicité' },
    { value: 'fournitures', label: 'Fournitures' }, { value: 'frais_bancaires', label: 'Frais bancaires' },
    { value: 'autre', label: 'Autre' },
];

const now = new Date();
const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

export default function ScanFacturesPage() {
    const [confirm, confirmDialog] = useConfirm();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const monthOptions = useMonthOptions();
    const [uploading, setUploading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [results, setResults] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [validating, setValidating] = useState({});
    const [searchDialog, setSearchDialog] = useState(null);
    const [searchQ, setSearchQ] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [dupeInfo, setDupeInfo] = useState(null); // {resultId, existing: [...], vehicle_id, fraisId}
    const [totalCheck, setTotalCheck] = useState(null); // {total_document_ttc, sum_lignes_ttc, difference, coherent}
    const [commercials, setCommercials] = useState([]);

    useEffect(() => {
        axios.get(`${API_URL}/api/scan/results`, { withCredentials: true }).then(r => setResults(r.data)).catch(() => {});
        axios.get(`${API_URL}/api/users`, { withCredentials: true }).then(r => {
            const comms = (r.data || []).filter(u => u.role === 'user');
            setCommercials(comms);
        }).catch(() => {});
    }, []);

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        setUploading(true);
        const formData = new FormData();
        for (const f of files) formData.append('files', f);
        try {
            const uploadRes = await axios.post(`${API_URL}/api/scan/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            const uploadResults = uploadRes.data.uploads || [];
            const successful = uploadResults.filter(u => u.id);
            const errors = uploadResults.filter(u => u.error);
            if (errors.length) {
                errors.forEach(er => {
                    if (er.error?.includes('trop volumineux')) toast.error(`${er.filename} : ${er.error}`);
                    else if (er.error?.includes('Type non supporté')) toast.error(`${er.filename} : format non supporté`);
                    else toast.error(`${er.filename} : ${er.error}`);
                });
            }
            if (!successful.length) { toast.error('Aucun fichier accepté'); setUploading(false); if (fileRef.current) fileRef.current.value = ''; return; }
            toast.success(`${successful.length} fichier(s) importé(s)`);
            setUploading(false);
            setAnalyzing(true);
            const allResults = [];
            let lastTotalCheck = null;
            for (const u of successful) {
                try {
                    const r = await axios.post(`${API_URL}/api/scan/${u.id}/analyze`, {}, { withCredentials: true });
                    allResults.push(...(r.data.results || []));
                    if (r.data.total_check) lastTotalCheck = r.data.total_check;
                } catch (err) {
                    const detail = err?.response?.data?.detail || '';
                    if (detail.includes('analyse')) toast.error(`Erreur analyse IA : ${u.filename}`);
                    else if (detail.includes('récupération')) toast.error(`Erreur récupération fichier : ${u.filename}`);
                    else toast.error(`Erreur analyse ${u.filename}`);
                }
            }
            setResults(prev => [...allResults, ...prev]);
            if (lastTotalCheck) setTotalCheck(lastTotalCheck);
            if (allResults.length) toast.success(`${allResults.length} facture(s) / ligne(s) analysée(s)`);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || '';
            if (status === 413 || detail.includes('volumineux')) toast.error('Photo trop volumineuse. Réduisez la résolution ou importez un fichier plus léger.');
            else if (status === 401 || status === 403) toast.error('Session expirée. Veuillez vous reconnecter.');
            else if (!navigator.onLine) toast.error('Connexion interrompue. Vérifiez votre réseau.');
            else toast.error(detail || 'Erreur lors de l\'envoi du fichier. Réessayez.');
        }
        setAnalyzing(false); setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
    };

    const updateField = (id, field, value) => setResults(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

    // Plate input: auto-search vehicle after typing
    const handlePlateChange = useCallback(async (id, plate) => {
        setResults(prev => prev.map(r => r.id === id ? { ...r, immatriculation_detectee: plate, ...(r.statut === 'doublon_detecte' ? { statut: 'en_attente', existing_frais: undefined, doublon_vehicle_id: undefined } : {}) } : r));
        if (plate.replace(/[\s-]/g, '').length < 4) {
            updateField(id, 'vehicle_match', { matched: false });
            return;
        }
        try {
            const res = await axios.post(`${API_URL}/api/controles-cg/search-vehicle?immatriculation=${encodeURIComponent(plate)}`, {}, { withCredentials: true });
            if (res.data.length === 1) {
                const v = res.data[0];
                updateField(id, 'vehicle_match', { matched: true, vehicle_id: v.id, immatriculation: v.immatriculation, nom: v.nom, modele: v.modele, commercial: v.commercial });
            } else if (res.data.length > 1) {
                updateField(id, 'vehicle_match', { matched: false, multiple: true, raw_plate: plate });
            } else {
                updateField(id, 'vehicle_match', { matched: false, raw_plate: plate });
            }
        } catch { updateField(id, 'vehicle_match', { matched: false, raw_plate: plate }); }
    }, []);

    const searchVehicle = async (q) => {
        setSearchQ(q);
        if (q.length < 2) { setSearchResults([]); return; }
        setSearchLoading(true);
        try { setSearchResults((await axios.post(`${API_URL}/api/controles-cg/search-vehicle?immatriculation=${encodeURIComponent(q)}`, {}, { withCredentials: true })).data); }
        catch { setSearchResults([]); }
        setSearchLoading(false);
    };

    const clearDoublon = (resultId) => setResults(prev => prev.map(r => r.id === resultId && r.statut === 'doublon_detecte' ? { ...r, statut: 'en_attente', existing_frais: undefined, doublon_vehicle_id: undefined } : r));
    const handleSelectVehicle = (resultId, v) => {
        clearDoublon(resultId);
        updateField(resultId, 'vehicle_match', { matched: true, vehicle_id: v.id, immatriculation: v.immatriculation, nom: v.nom, modele: v.modele, commercial: v.commercial });
        updateField(resultId, 'type_frais_suggere', 'vehicule');
        updateField(resultId, 'immatriculation_detectee', v.immatriculation);
        setSearchDialog(null); setSearchQ('');
    };

    const handleUnflagRescan = async (item) => {
        if (!(await confirm('Confirmer qu\'il s\'agit d\'une facture différente de celle déjà scannée ?'))) return;
        try {
            await axios.post(`${API_URL}/api/scan/results/${item.id}/unflag-rescan`, {}, { withCredentials: true });
            setResults(prev => prev.map(r => r.id === item.id ? { ...r, statut: 'en_attente' } : r));
            toast.success('Facture remise en traitement');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur — aucune modification enregistrée'); }
    };

    const eur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);
    const openDupe = (item, data) => setDupeInfo({
        resultId: item.id,
        existing: data.existing_frais || [],
        vehicle_id: data.vehicle_id || data.doublon_vehicle_id || item.vehicle_match?.vehicle_id,
        vehicle_nom: data.vehicle_nom || item.vehicle_match?.nom || '',
        scanned_ttc: Number(item.montant_ttc) || 0,
    });

    const handleValidate = async (item) => {
        if (item.statut === 'doublon_detecte' && item.existing_frais?.length) { openDupe(item, item); return; }
        setValidating(v => ({ ...v, [item.id]: true }));
        try {
            const dest = item.destination_suggeree || item.type_frais_suggere || 'frais_vehicule';
            const payload = { destination: dest, immatriculation: item.immatriculation_detectee || '' };
            if (dest === 'frais_mutualise') payload.mois_affectation = item._mois_affectation || currentMonth;
            if (dest === 'retenue_commercial') {
                payload.commercial_id = item._commercial_id || '';
                payload.mois_concerne = item._mois_affectation || currentMonth;
            }
            // Save edits first
            await axios.put(`${API_URL}/api/scan/results/${item.id}`, {
                fournisseur: item.fournisseur, numero_facture: item.numero_facture, date_facture: item.date_facture,
                montant_ttc: item.montant_ttc, taux_tva: item.taux_tva, tva_recuperable: item.tva_recuperable,
                description: item.description, categorie: item.categorie, type_frais_suggere: item.type_frais_suggere,
                destination_suggeree: dest, immatriculation_detectee: item.immatriculation_detectee,
            }, { withCredentials: true });
            let res = await axios.post(`${API_URL}/api/scan/results/${item.id}/validate`, payload, { withCredentials: true });
            if (res.data.status === 'doublon_document') {
                if (!(await confirm(`${res.data.message} (${res.data.twin?.fournisseur || ''} ${res.data.twin?.montant_ttc ?? ''} €).\n\nValider quand même cette ligne ?`, { title: 'Doublon dans le même document', confirmLabel: 'Valider quand même', danger: true }))) {
                    setValidating(v => ({ ...v, [item.id]: false }));
                    return 'doublon';
                }
                res = await axios.post(`${API_URL}/api/scan/results/${item.id}/validate`, { ...payload, force_meme_document: true }, { withCredentials: true });
            }
            if (res.data.status === 'doublon_detecte') {
                setResults(prev => prev.map(r => r.id === item.id ? { ...r, statut: 'doublon_detecte', existing_frais: res.data.existing_frais, doublon_vehicle_id: res.data.vehicle_id } : r));
                openDupe(item, res.data);
                setValidating(v => ({ ...v, [item.id]: false }));
                return 'doublon';
            } else if (res.data.status === 'doublon_agence' || res.data.status === 'doublon_retenue') {
                toast.warning(res.data.message || 'Doublon détecté');
                setResults(prev => prev.filter(r => r.id !== item.id));
            } else {
                const msgs = {
                    frais_vehicule: res.data.pending ? 'Frais enregistré en attente de rattachement' : 'Frais véhicule créé et rattaché',
                    frais_agence: 'Charge d\'agence enregistrée',
                    retenue_commercial: 'Retenue commerciale enregistrée',
                    frais_mutualise: 'Frais mutualisé enregistré',
                };
                toast.success(msgs[dest] || 'Validé');
                setResults(prev => prev.filter(r => r.id !== item.id));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur validation'); }
        setValidating(v => ({ ...v, [item.id]: false }));
    };

    const [dupeBusy, setDupeBusy] = useState(false);
    const dupeAction = async (path, body, okMsg) => {
        if (!dupeInfo || dupeBusy) return;
        setDupeBusy(true);
        try {
            const res = await axios.post(`${API_URL}/api/scan/results/${dupeInfo.resultId}/${path}`, body, { withCredentials: true });
            toast.success(typeof okMsg === 'function' ? okMsg(res.data) : okMsg);
            setResults(prev => prev.filter(r => r.id !== dupeInfo.resultId));
            setDupeInfo(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur — aucune modification enregistrée');
        }
        setDupeBusy(false);
    };
    const handleForceAttach = async () => {
        if (!(await confirm('Créer une NOUVELLE dépense malgré la dépense similaire déjà présente ?', { confirmLabel: 'Créer la dépense' }))) return;
        dupeAction('force-attach', { vehicle_id: dupeInfo.vehicle_id }, 'Nouvelle dépense enregistrée et justificatif rattaché');
    };
    const handleLinkExisting = (f) => dupeAction('link-existing', { vehicle_id: dupeInfo.vehicle_id, frais_idx: f.idx, expected_nom: f.nom ?? '' }, 'Justificatif lié à la dépense existante — aucune dépense ajoutée');
    const handleReplaceExisting = (f) => dupeAction('replace-existing', { vehicle_id: dupeInfo.vehicle_id, frais_idx: f.idx, expected_nom: f.nom ?? '' }, (d) => `Frais remplacé (${d.old_montant}€ → ${d.new_montant}€)`);
    const handleDocOnly = () => dupeAction('attach-document', { vehicle_id: dupeInfo.vehicle_id }, (d) => `Justificatif rattaché au dossier ${d.vehicle_nom || ''} — aucune dépense`);
    const handleDocOnlyItem = async (item) => {
        setValidating(v => ({ ...v, [item.id]: true }));
        try {
            const r = await axios.post(`${API_URL}/api/scan/results/${item.id}/attach-document`, { vehicle_id: item.vehicle_match?.vehicle_id }, { withCredentials: true });
            toast.success(`Justificatif rattaché au dossier ${r.data.vehicle_nom || ''} — aucune dépense`);
            setResults(prev => prev.filter(r2 => r2.id !== item.id));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur — aucune modification enregistrée'); }
        setValidating(v => ({ ...v, [item.id]: false }));
    };

    const handleDismiss = async (id) => {
        try { await axios.delete(`${API_URL}/api/scan/results/${id}`, { withCredentials: true }); setResults(prev => prev.filter(r => r.id !== id)); } catch { toast.error('Erreur'); }
    };

    const handleValidateAll = async () => {
        const ready = results.filter(r => r.confiance === 'pret' && r.statut === 'en_attente');
        let dupes = 0;
        for (const item of ready) { if ((await handleValidate(item)) === 'doublon') dupes += 1; }
        if (dupes > 1) toast.warning(`${dupes} doublons à résoudre — bouton « Résoudre le doublon » sur chaque facture`);
    };

    const readyCount = results.filter(r => r.confiance === 'pret' && r.statut === 'en_attente').length;
    const canValidate = (item) => {
        const dest = item.destination_suggeree || item.type_frais_suggere;
        if (dest === 'frais_vehicule' || dest === 'vehicule') {
            const plate = (item.immatriculation_detectee || '').replace(/[\s-]/g, '');
            return plate.length >= 4 && item.montant_ttc > 0;
        }
        if (dest === 'frais_mutualise' || dest === 'mutualise') return item.montant_ttc > 0;
        if (dest === 'frais_agence' || dest === 'agence') return item.montant_ttc > 0;
        if (dest === 'retenue_commercial') return item.montant_ttc > 0 && !!item._commercial_id;
        return item.montant_ttc > 0;
    };

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="scan-page">
            {confirmDialog}
            <button onClick={() => navigate('/frais-vehicules')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-5" data-testid="scan-back">
                <ArrowLeft className="w-4 h-4" />Retour
            </button>
            <h1 className="text-xl font-semibold text-gray-900 mb-1">Scanner intelligent</h1>
            <p className="text-sm text-gray-400 mb-6">Importez vos factures — Marge Auto les analyse et propose la bonne destination.</p>

            {/* Upload */}
            <div className="card p-5 sm:p-6 mb-6 border-2 border-dashed border-gray-200 hover:border-[#6366F1]/40 transition-colors text-center" data-testid="scan-upload-zone">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/jpg,image/heic,image/heif,image/webp,application/pdf" multiple onChange={handleUpload} className="hidden" data-testid="scan-file-input" />
                {(uploading || analyzing) ? (
                    <div className="py-6 flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
                        <p className="text-sm text-gray-500">{uploading ? 'Import...' : 'Analyse IA...'}</p>
                    </div>
                ) : (
                    <div className="py-4">
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-3">
                            <Button onClick={() => fileRef.current?.click()} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white h-12 sm:h-11 px-6 text-sm rounded-xl w-full sm:w-auto" data-testid="scan-upload-btn">
                                <Upload className="w-4 h-4 mr-2" />Importer des factures
                            </Button>
                            <Button onClick={() => { if (fileRef.current) { fileRef.current.setAttribute('capture', 'environment'); fileRef.current.removeAttribute('multiple'); fileRef.current.click(); setTimeout(() => { fileRef.current?.removeAttribute('capture'); fileRef.current?.setAttribute('multiple', ''); }, 500); } }}
                                variant="outline" className="h-12 sm:h-11 px-6 text-sm rounded-xl border-[#6366F1]/30 text-[#6366F1] w-full sm:w-auto" data-testid="scan-camera-btn">
                                <Camera className="w-4 h-4 mr-2" />Prendre en photo
                            </Button>
                        </div>
                        <p className="text-xs text-gray-400">JPG, PNG, HEIC, WebP, PDF — Multi-fichiers et photos iPhone</p>
                    </div>
                )}
            </div>

            {readyCount > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4 bg-emerald-50 rounded-lg px-4 py-3 border border-emerald-200">
                    <span className="text-sm text-emerald-700 font-medium">{readyCount} prêt(s)</span>
                    <Button onClick={handleValidateAll} className="bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] h-9 px-4 w-full sm:w-auto" data-testid="scan-validate-all">
                        <Check className="w-4 h-4 mr-1.5" />Valider les {readyCount} prêts
                    </Button>
                </div>
            )}

            {/* Total document consistency check */}
            {totalCheck && (
                <div className={`flex items-center gap-3 mb-4 rounded-lg px-4 py-3 border ${totalCheck.coherent ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                    {totalCheck.coherent
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />}
                    <div className="text-sm">
                        <span className="font-medium">{totalCheck.coherent ? 'Total cohérent' : 'Total à vérifier'}</span>
                        <span className="text-gray-500 ml-2">Document : {fmt(totalCheck.total_document_ttc)} — Lignes : {fmt(totalCheck.sum_lignes_ttc)}</span>
                        {!totalCheck.coherent && <span className="text-amber-700 ml-2">(écart : {fmt(totalCheck.difference)})</span>}
                    </div>
                </div>
            )}

            <DocumentsToIdentify />

            {results.length === 0 && !uploading && !analyzing && (
                <div className="text-center py-16 text-gray-400 text-sm">Aucune facture en attente</div>
            )}

            <div className="space-y-3" data-testid="scan-results-list">
                {results.map(item => {
                    const conf = CONF[item.confiance] || CONF.a_verifier;
                    const ConfIcon = conf.Icon;
                    const expanded = expandedId === item.id;
                    const dest = item.destination_suggeree || item.type_frais_suggere || 'frais_vehicule';
                    const isV = dest === 'frais_vehicule' || dest === 'vehicule';
                    const isM = dest === 'frais_mutualise' || dest === 'mutualise';
                    const isA = dest === 'frais_agence' || dest === 'agence';
                    const isR = dest === 'retenue_commercial';
                    const matched = item.vehicle_match?.matched;
                    const hasPlate = (item.immatriculation_detectee || '').replace(/[\s-]/g, '').length >= 4;
                    const hasDupe = item.pre_duplicate && item.pre_duplicate.length > 0;
                    const destOpt = DEST_OPTIONS.find(d => d.value === dest) || DEST_OPTIONS[0];
                    return (
                        <div key={item.id} className="card overflow-hidden" data-testid={`scan-result-${item.id}`}>
                            {item.statut === 'deja_scannee' && (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 sm:px-4 py-2.5 bg-amber-50 border-b border-amber-200" data-testid={`scan-rescan-${item.id}`}>
                                    <p className="text-[12px] text-amber-900 flex-1">Cette facture a déjà été scannée (même fournisseur, numéro et montant). Aucune dépense ne sera créée.</p>
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={() => handleDismiss(item.id)} className="h-8 text-[12px] bg-amber-600 hover:bg-amber-700 text-white" data-testid={`scan-rescan-dismiss-${item.id}`}>Ignorer le doublon</Button>
                                        <Button size="sm" variant="outline" onClick={() => handleUnflagRescan(item)} className="h-8 text-[12px] bg-white" data-testid={`scan-rescan-keep-${item.id}`}>Facture différente</Button>
                                    </div>
                                </div>
                            )}
                            {/* Header row */}
                            <div className="flex items-center gap-2 px-3 sm:px-4 py-3 cursor-pointer hover:bg-gray-50/50" onClick={() => setExpandedId(expanded ? null : item.id)}>
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-medium border shrink-0 ${conf.color}`}>
                                    <ConfIcon className="w-3 h-3" /><span className="hidden sm:inline">{conf.label}</span>
                                </span>
                                <span className="text-[13px] font-medium text-gray-900 truncate flex-1 min-w-0">{item.fournisseur || 'Fournisseur ?'}</span>
                                {isV && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full border border-blue-200 shrink-0"><Car className="w-3 h-3" />Véhicule</span>}
                                {isM && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full border border-indigo-200 shrink-0"><Layers className="w-3 h-3" />Mutualisé</span>}
                                {isA && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded-full border border-orange-200 shrink-0"><Building2 className="w-3 h-3" />Agence</span>}
                                {isR && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded-full border border-purple-200 shrink-0"><UserRound className="w-3 h-3" />Retenue</span>}
                                {hasDupe && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200 shrink-0"><AlertTriangle className="w-3 h-3" />Doublon ?</span>}
                                <span className="font-mono font-semibold text-gray-900 text-[13px] shrink-0 whitespace-nowrap">{fmt(item.montant_ttc)}</span>
                                {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                            </div>

                            {expanded && (
                                <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-gray-100 space-y-4">
                                    {/* Fields — single column on mobile, grid on desktop */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
                                        <Field label="Fournisseur" value={item.fournisseur} onChange={v => updateField(item.id, 'fournisseur', v)} />
                                        <Field label="N° facture" value={item.numero_facture} onChange={v => updateField(item.id, 'numero_facture', v)} />
                                        <div><label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">Date facture</label>
                                            <DatePickerField value={item.date_facture} onChange={v => updateField(item.id, 'date_facture', v)} className="h-9 text-[12px]" testId={`scan-date-${item.id}`} /></div>
                                        <Field label="Montant TTC (€)" value={item.montant_ttc} onChange={v => updateField(item.id, 'montant_ttc', parseFloat(v) || 0)} type="number" mono />
                                        <Field label="Taux TVA (%)" value={item.taux_tva} onChange={v => updateField(item.id, 'taux_tva', parseFloat(v) || 20)} type="number" />
                                        <div>
                                            <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">TVA récupérable</label>
                                            <Select value={item.tva_recuperable ? 'oui' : 'non'} onValueChange={v => updateField(item.id, 'tva_recuperable', v === 'oui')}>
                                                <SelectTrigger className="h-9 text-[12px]"><SelectValue /></SelectTrigger>
                                                <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <Field label="Description" value={item.description} onChange={v => updateField(item.id, 'description', v)} full />

                                    {/* Destination selector — 4 choices */}
                                    <div>
                                        <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1.5">Destination</label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {DEST_OPTIONS.map(d => {
                                                const active = dest === d.value;
                                                return (
                                                    <button key={d.value} onClick={() => { updateField(item.id, 'destination_suggeree', d.value); updateField(item.id, 'type_frais_suggere', d.value === 'frais_vehicule' ? 'vehicule' : d.value === 'frais_mutualise' ? 'mutualise' : d.value); }}
                                                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${active ? d.color : d.colorInactive}`}
                                                        data-testid={`scan-dest-${d.value}`}>
                                                        <d.Icon className="w-3.5 h-3.5" />{d.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* FRAIS VÉHICULE fields */}
                                    {isV && (
                                        <div className="space-y-2">
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <div className="flex-1">
                                                    <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">Immatriculation</label>
                                                    <Input value={item.immatriculation_detectee || ''} onChange={e => handlePlateChange(item.id, e.target.value)}
                                                        placeholder="AB-123-CD" className="h-10 text-sm font-mono uppercase" data-testid="scan-plate-input" />
                                                </div>
                                                <div className="sm:pt-5">
                                                    <Button variant="outline" size="sm" onClick={() => { setSearchDialog(item.id); setSearchQ(item.immatriculation_detectee || ''); searchVehicle(item.immatriculation_detectee || ''); }}
                                                        className="h-10 px-3 text-[12px] w-full sm:w-auto"><Search className="w-3.5 h-3.5 mr-1" />Rechercher</Button>
                                                </div>
                                            </div>
                                            {matched && (
                                                <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-[13px] font-medium text-emerald-800 truncate">{item.vehicle_match.immatriculation} — {item.vehicle_match.nom}</p>
                                                        {item.vehicle_match.commercial && <p className="text-[11px] text-emerald-600">{item.vehicle_match.commercial}</p>}
                                                    </div>
                                                </div>
                                            )}
                                            {!matched && hasPlate && (
                                                <div className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                                                    <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                                                    <p className="text-[12px] text-amber-700">Véhicule non encore créé — le frais sera conservé en attente et rattaché automatiquement.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* FRAIS MUTUALISÉ fields */}
                                    {isM && (
                                        <div className="w-full sm:w-48">
                                            <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">Mois d'affectation</label>
                                            <Select value={item._mois_affectation || currentMonth} onValueChange={v => updateField(item.id, '_mois_affectation', v)}>
                                                <SelectTrigger className="h-9 text-[12px]"><SelectValue /></SelectTrigger>
                                                <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    {/* RETENUE COMMERCIAL fields */}
                                    {isR && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">Commercial *</label>
                                                <Select value={item._commercial_id || ''} onValueChange={v => updateField(item.id, '_commercial_id', v)}>
                                                    <SelectTrigger className="h-9 text-[12px]" data-testid="scan-commercial-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                                                    <SelectContent>{commercials.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                                </Select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">Mois d'imputation</label>
                                                <Select value={item._mois_affectation || currentMonth} onValueChange={v => updateField(item.id, '_mois_affectation', v)}>
                                                    <SelectTrigger className="h-9 text-[12px]"><SelectValue /></SelectTrigger>
                                                    <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    )}

                                    {/* Category — dynamic based on destination */}
                                    <div className="w-full sm:w-56">
                                        <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">Catégorie</label>
                                        <Select value={item.categorie || 'autre'} onValueChange={v => updateField(item.id, 'categorie', v)}>
                                            <SelectTrigger className="h-9 text-[12px]"><SelectValue /></SelectTrigger>
                                            <SelectContent>{(isV ? CATEGORIES_FV : isM ? CATEGORIES_FM : isA ? CATEGORIES_AGENCE : isR ? [{value:'frais_divers',label:'Frais divers'},{value:'acompte',label:'Acompte'},{value:'autre',label:'Autre'}] : [...new Map([...CATEGORIES_FV, ...CATEGORIES_FM, ...CATEGORIES_AGENCE].map(c=>[c.value,c])).values()]).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-gray-100">
                                        {item.scan_document_id && (
                                            <Button variant="outline" size="sm" className="h-9 text-[12px] w-full sm:w-auto" onClick={() => window.open(`${API_URL}/api/scan/documents/${item.scan_document_id}/file`, '_blank')}>
                                                <Eye className="w-3.5 h-3.5 mr-1" />Voir la facture
                                            </Button>
                                        )}
                                        <div className="flex-1" />
                                        <div className="flex flex-wrap gap-2">
                                            {isV && matched && (
                                                <Button variant="outline" size="sm" disabled={validating[item.id]} onClick={() => handleDocOnlyItem(item)} className="h-9 text-[12px] flex-1 sm:flex-none" data-testid="scan-doc-only-btn">
                                                    Justificatif seul
                                                </Button>
                                            )}
                                            <Button variant="ghost" size="sm" className="h-9 text-[12px] text-red-400 hover:text-red-600 flex-1 sm:flex-none" onClick={() => handleDismiss(item.id)} data-testid="scan-dismiss">
                                                <Trash2 className="w-3.5 h-3.5 mr-1" />Ignorer
                                            </Button>
                                            <Button onClick={() => handleValidate(item)} disabled={validating[item.id] || !canValidate(item) || item.statut === 'deja_scannee'}
                                                className="h-9 px-4 text-[12px] bg-[#6366F1] hover:bg-[#4F46E5] text-white flex-1 sm:flex-none" data-testid="scan-validate-btn">
                                                {validating[item.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : (isV && !matched && hasPlate) ? <Clock className="w-3.5 h-3.5 mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                                                {item.statut === 'doublon_detecte' ? 'Résoudre le doublon' : (isV && !matched && hasPlate) ? 'En attente' : isA ? 'Charge agence' : isR ? 'Retenue' : 'Valider'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Vehicle Search Dialog */}
            <Dialog open={!!searchDialog} onOpenChange={v => { if (!v) setSearchDialog(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Sélectionner un véhicule</DialogTitle></DialogHeader>
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <Input placeholder="Recherche plaque..." value={searchQ} onChange={e => searchVehicle(e.target.value)} className="pl-9 font-mono" autoFocus data-testid="scan-vehicle-search" />
                    </div>
                    {searchLoading && <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />}
                    <div className="max-h-56 overflow-y-auto space-y-1">
                        {searchResults.map(v => (
                            <button key={v.id} onClick={() => handleSelectVehicle(searchDialog, v)}
                                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[#6366F1]/5 border border-gray-100">
                                <p className="text-[13px] font-medium">{v.immatriculation} — {v.nom || v.modele}</p>
                                <p className="text-[11px] text-gray-400">{v.commercial || 'Aucun commercial'}</p>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Duplicate Warning with enriched actions */}
            <Dialog open={!!dupeInfo} onOpenChange={v => { if (!v && !dupeBusy) setDupeInfo(null); }}>
                <DialogContent className="max-w-lg w-[94vw] p-0 gap-0 overflow-hidden bg-white border border-gray-200 rounded-2xl [&>*]:min-w-0" data-testid="scan-dupe-dialog">
                    <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100 bg-white">
                        <DialogTitle className="flex items-center gap-2 text-amber-700 text-base"><AlertTriangle className="w-5 h-5 shrink-0" />Dépense déjà enregistrée ?</DialogTitle>
                    </DialogHeader>
                    <div className="p-4 sm:p-5 space-y-4 bg-white max-h-[75vh] overflow-y-auto">
                        <p className="text-sm text-gray-700">Attention, cette dépense semble déjà enregistrée sur {dupeInfo?.vehicle_nom ? <b>{dupeInfo.vehicle_nom}</b> : 'ce véhicule'}. Aucune dépense n'a été créée.</p>
                        <div className="space-y-2">
                            {dupeInfo?.existing?.map((f, i) => {
                                const isSameAmount = Math.abs((f.montant_ttc || 0) - (dupeInfo?.scanned_ttc || 0)) < 0.05;
                                return (
                                    <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-2 bg-white">
                                        <div className="flex items-start justify-between gap-2 text-sm">
                                            <span className="text-gray-900 min-w-0 break-words">{f.nom || 'Frais'}</span>
                                            <span className="font-mono font-semibold text-gray-900 shrink-0">{eur(f.montant_ttc)}</span>
                                        </div>
                                        <p className="text-[11px] text-gray-500">
                                            <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${f.niveau === 'confirme' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>{f.niveau === 'confirme' ? 'Doublon confirmé' : 'Doublon probable'}</span>
                                            {f.lie_facture ? 'Déjà liée à une facture' : 'Saisie manuelle, sans facture'}{isSameAmount ? ' · même montant' : ` · montant différent (${eur(f.montant_ttc)} / facture ${eur(dupeInfo?.scanned_ttc)})`}
                                        </p>
                                        <div className="flex flex-col gap-1.5">
                                            <button disabled={dupeBusy} onClick={() => handleLinkExisting(f)} className="w-full text-left px-3 py-2.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-[13px] font-medium border border-emerald-200 transition-colors disabled:opacity-50" data-testid={`scan-link-btn-${i}`}>
                                                Lier la facture à cette dépense (aucune dépense ajoutée)
                                            </button>
                                            {!isSameAmount && (
                                                <button disabled={dupeBusy} onClick={() => handleReplaceExisting(f)} className="w-full text-left px-3 py-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-800 text-[13px] font-medium border border-indigo-200 transition-colors disabled:opacity-50" data-testid={`scan-replace-btn-${i}`}>
                                                    Remplacer ({eur(f.montant_ttc)} → {eur(dupeInfo?.scanned_ttc)})
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <button disabled={dupeBusy} onClick={handleDocOnly} className="w-full text-left px-3 py-2.5 rounded-lg bg-white hover:bg-gray-50 text-gray-800 text-[13px] font-medium border border-gray-300 transition-colors disabled:opacity-50" data-testid="scan-doc-only-dupe-btn">
                            Rattacher uniquement le justificatif au dossier (sans dépense)
                        </button>
                        <div className="flex flex-col sm:flex-row gap-2 pt-1">
                            <Button variant="outline" disabled={dupeBusy} onClick={() => setDupeInfo(null)} className="flex-1 h-10 text-[13px]" data-testid="scan-dupe-later">Décider plus tard</Button>
                            <Button variant="outline" disabled={dupeBusy} onClick={handleForceAttach} className="flex-1 h-10 text-[13px] text-red-600 border-red-200 hover:bg-red-50" data-testid="scan-force-attach">
                                {dupeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer quand même une dépense'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', mono = false, full = false }) {
    return (
        <div className={full ? 'col-span-full' : ''}>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1">{label}</label>
            <Input type={type} step={type === 'number' ? '0.01' : undefined} inputMode={type === 'number' ? 'decimal' : undefined}
                value={value || ''} onChange={e => onChange(e.target.value)}
                className={`h-9 text-[12px] w-full ${mono ? 'font-mono' : ''}`} />
        </div>
    );
}
